import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';

import { MODAL_DATA, MODAL_REF, ModalRef } from '@services/components/ModalService/modal.ref';
import { Order, OrderAddress, OrderInvoice, OrderItem, OrderShippingPackage, OrderShippingQuote, OrderStatus } from '@services/orders/interfaces/orders.interfaces';
import { OrderStatusBadgeComponent } from '@shared/components/UI/order-status-badge/order-status-badge.component';
import { ClpCurrencyPipe } from '@shared/pipes/clp-currency.pipe';
import { formatRut } from '@shared/utils/rut.utils';
import { Utils } from '@shared/utils/utils';

import { CHILEXPRESS_TRACKING_CODE_SEPARATOR, CHILEXPRESS_TRACKING_URL, ORDER_DATE_FORMAT, selectableStatusesFor, statusLabelFor } from '@constants/orders.constants';

export interface OrderDetailModalData {
  order: Order;
}

const EMPTY_VALUE = '—';
const COPY_FEEDBACK_DURATION_MS = 2500;

type ChilexpressCopyState = 'idle' | 'copied' | 'failed';

@Component({
  selector: 'app-order-detail-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, OrderStatusBadgeComponent, ClpCurrencyPipe],
  templateUrl: './order-detail-modal.component.html',
  styleUrl: './order-detail-modal.component.css',
})
export class OrderDetailModalComponent {
  public readonly formatRut = formatRut;
  public readonly selectedStatus = signal<OrderStatus | null>(null);

  protected readonly data = inject<OrderDetailModalData>(MODAL_DATA);

  protected readonly dateFormat = ORDER_DATE_FORMAT;

  protected readonly chilexpressCopyState = signal<ChilexpressCopyState>('idle');

  private copyFeedbackTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly modalRef = inject(MODAL_REF) as ModalRef<OrderStatus>;
  private readonly utils = inject(Utils);
  private readonly destroyRef = inject(DestroyRef);

  public get availableStatuses(): OrderStatus[] {
    return selectableStatusesFor(this.order.status, this.order.shippingType).filter((status) => status !== this.order.status);
  }

  public get canChangeStatus(): boolean {
    return this.order.paymentStatus === 'paid' && this.availableStatuses.length > 0;
  }

  protected get order(): Order {
    return this.data.order;
  }

  protected get isPickup(): boolean {
    return this.order.shippingType === 'pickup';
  }

  /**
   * El bloque del motivo se muestra en los dos estados que lo admiten, con o
   * sin texto: que esté vacío también es información. El título cambia para no
   * decir "motivo de la cancelación" en una devolución.
   */
  protected get statusReasonLabel(): string | null {
    if (this.order.status === 'returned') {
      return 'Motivo de la devolución';
    }

    return this.order.status === 'cancelled' ? 'Motivo de la cancelación' : null;
  }

  /** En devoluciones el motivo es opcional, así que puede venir vacío. */
  protected get statusReasonText(): string {
    return this.order.statusReason?.trim() || 'Detalle no especificado';
  }

  protected get hasStatusReason(): boolean {
    return !!this.order.statusReason?.trim();
  }

  /** Unresolved payment: only the TUU callback or the 30 minute window closes it. */
  protected get isPaymentPending(): boolean {
    return this.order.paymentStatus === 'pending';
  }

  protected get isPaymentUnconfirmed(): boolean {
    return this.order.paymentStatus === 'unconfirmed';
  }

  protected get invoice(): OrderInvoice | null {
    return this.order.invoice;
  }

  /**
   * El back solo manda `invoice.address` cuando el cliente pidió una dirección
   * de facturación distinta a la del envío; si es null, usan la misma.
   */
  protected get invoiceAddress(): OrderAddress | null {
    return this.order.invoice?.address ?? null;
  }

  /** Recordatorio de a dónde va el paquete, para no confundirlo con la factura. */
  protected get shippingSummary(): string {
    const shipping = this.order.shipping;

    return this.join([shipping?.street, this.communeOf(shipping), this.regionOf(shipping)], ', ');
  }

  protected get shippingQuote(): OrderShippingQuote | null {
    return this.order.shippingQuote ?? null;
  }

  protected get trackingLinks(): Array<{ trackingCode: string; trackingUrl: string }> {
    return (this.order.trackingCode ?? '')
      .split(CHILEXPRESS_TRACKING_CODE_SEPARATOR)
      .filter(Boolean)
      .map((trackingCode) => ({ trackingCode, trackingUrl: `${CHILEXPRESS_TRACKING_URL}${encodeURIComponent(trackingCode)}` }));
  }

  public statusLabel(status: OrderStatus): string {
    return statusLabelFor(status, this.order.shippingType);
  }

  public selectStatus(value: string): void {
    this.selectedStatus.set(this.availableStatuses.find((status) => status === value) ?? null);
  }

  public confirmStatusChange(): void {
    const status = this.selectedStatus();
    if (this.canChangeStatus && status && this.availableStatuses.includes(status)) {
      this.modalRef.close({ reason: 'confirm', data: status });
    }
  }

  public closeDetail(): void {
    this.modalRef.close({ reason: 'cancel' });
  }

  protected streetOf(address: OrderAddress | null | undefined): string {
    return address?.street || EMPTY_VALUE;
  }

  protected communeOf(address: OrderAddress | null | undefined): string {
    return address?.communeName || address?.communeCode || EMPTY_VALUE;
  }

  protected regionOf(address: OrderAddress | null | undefined): string {
    return address?.regionName || address?.regionCode || EMPTY_VALUE;
  }

  /** Villa, sector o población: opcional, solo se muestra si el cliente lo cargó. */
  protected localityOf(address: OrderAddress | null | undefined): string | null {
    return address?.locality || null;
  }

  /**
   * Detalle de la variante: los llaveros se describen por el nombre grabado, el
   * grosor y su color (o la combinación de dos), el resto por talla y color.
   */
  protected itemVariant(item: OrderItem): string {
    if (item.productType === 'keychain') {
      const color =
        item.colorName ??
        (item.frontColorName && item.backColorName ? `${item.frontColorName}/${item.backColorName}` : null);
      return this.join([item.customName, item.thickness, color]);
    }

    return this.join([item.sizeLabel, item.colorName]);
  }

  protected packageMeasures(shippingPackage: OrderShippingPackage): string {
    return `${shippingPackage.lengthCm} × ${shippingPackage.widthCm} × ${shippingPackage.heightCm} cm`;
  }

  protected packageWeight(shippingPackage: OrderShippingPackage): string {
    return `${(shippingPackage.weightGrams / 1000).toLocaleString('es-CL', { maximumFractionDigits: 2 })} kg`;
  }

  /** Texto plano para pegar en el formulario de la web de Chilexpress. */
  protected copyChilexpressData(): void {
    const packagesText = (this.shippingQuote?.packages ?? []).map((shippingPackage, index) =>
      `Bulto ${index + 1}: caja ${shippingPackage.boxLabel}, ${this.packageMeasures(shippingPackage)}, ${this.packageWeight(shippingPackage)}, valor declarado ${this.utils.formatCurrency(shippingPackage.declaredValue)}`,
    );
    const chilexpressText = [
      `Destinatario: ${this.order.customer.name}`,
      `Teléfono: ${this.order.customer.phone ?? EMPTY_VALUE}`,
      `Correo: ${this.order.customer.email}`,
      `Dirección: ${this.shippingSummary}`,
      ...(this.localityOf(this.order.shipping) ? [`Villa o sector: ${this.localityOf(this.order.shipping)}`] : []),
      ...packagesText,
    ].join('\n');

    const clipboard = globalThis.navigator?.clipboard;
    if (!clipboard) {
      this.showCopyFeedback('failed');
      return;
    }

    clipboard.writeText(chilexpressText)
      .then(() => this.showCopyFeedback('copied'))
      .catch(() => this.showCopyFeedback('failed'));
  }

  private join(parts: (string | null | undefined)[], separator = ' · '): string {
    return parts.filter((part) => part && part !== EMPTY_VALUE).join(separator) || EMPTY_VALUE;
  }

  private showCopyFeedback(copyState: Exclude<ChilexpressCopyState, 'idle'>): void {
    this.chilexpressCopyState.set(copyState);

    if (this.copyFeedbackTimeout === null) {
      this.destroyRef.onDestroy(() => this.copyFeedbackTimeout && clearTimeout(this.copyFeedbackTimeout));
    } else {
      clearTimeout(this.copyFeedbackTimeout);
    }
    this.copyFeedbackTimeout = setTimeout(() => this.chilexpressCopyState.set('idle'), COPY_FEEDBACK_DURATION_MS);
  }
}
